import React, { useRef, useEffect, useMemo } from 'react'
import { Grid, TextField, Typography } from '@mui/material'
import clsx from 'clsx'
import { useStyles } from './CheckinModal.styles'
import { createLogger } from '../../../../app/logger'
import { useResponsiveInfo } from '../../../../hooks/useResponsiveInfo'
import {
  getBibFieldErrorSelector,
  getDirtyBibSelector,
  getSelectedEntrySelector,
  getTagFieldErrorSelector,
  isBibError,
  isBibZeroError,
  isTagError,
  setDirtyBib,
  setDirtyInput,
  setEnterKeyPress,
} from '../../checkinSlice'
import useAppDispatch from '../../../../hooks/useAppDispatch'
import useAppSelector from '../../../../hooks/useAppSelector'
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined'
import { ChronoIconButton } from '@eventops/chronotrack-ctlive-mui'
import useCheckCameraAccess from '../../../camera/hooks/useCheckCameraAccess'
import { extractBibValue } from '../../../../utils/extractBibValue'
import { debounce } from 'lodash'
import useCheckinSettings from '../../hooks/useCheckinSettings'

// eslint-disable-next-line no-unused-vars
const logger = createLogger({
  prefix: 'EntryPanel',
})

// ==================================================================================================

export const EntryPanelBibField = () => {
  const dispatch = useAppDispatch()
  const entry = useAppSelector(getSelectedEntrySelector)
  const { classes } = useStyles()
  const { isMobile } = useResponsiveInfo()
  const ref = useRef<HTMLDivElement>(null)
  const bibFieldError = useAppSelector(getBibFieldErrorSelector)
  const tagFieldError = useAppSelector(getTagFieldErrorSelector)
  const { checkinAllSettings } = useCheckinSettings()
  const askForTag = checkinAllSettings.settings?.askForTag
  // when askForTag is false, the bib doubles as the tag (backend fallback), so a
  // tag conflict must surface here since there's no separate tag field to show it
  const error =
    isBibError(bibFieldError) ||
    isBibZeroError(bibFieldError) ||
    (!askForTag && isTagError(tagFieldError))
  const checkCameraAccess = useCheckCameraAccess()
  const dirtyBib = useAppSelector(getDirtyBibSelector) || ''
  logger.debug('BibField', { dirtyBib })

  const debounceDirtyBib = useMemo(
    () => debounce((value) => {
      let updatedValue = value
      if (updatedValue.includes('/bib/')) {
        updatedValue = updatedValue.split('/bib/')[1]
      }
      updatedValue = extractBibValue(updatedValue)

      dispatch(setDirtyBib(updatedValue))
      dispatch(setDirtyInput(!!updatedValue))
    }, 100, { leading: false, trailing: true })
    , [dispatch])

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!entry?.checkedIn && e.key === 'Enter') {
        dispatch(setEnterKeyPress(true))
        debounceDirtyBib.flush()
      } else {
        dispatch(setEnterKeyPress(false))
      }
    },
    [entry?.checkedIn, dispatch, debounceDirtyBib]
  )

  useEffect(() => {
    if (dirtyBib && !entry?.checkedIn) {
      dispatch(setDirtyInput(true))
    }
    if (ref.current) {
      ref.current.focus()
    }
  }, [dirtyBib, entry, dispatch, ref])

  if (!!entry?.checkedIn && !error) {
    return (
      <Grid
        data-cy='checked-in-grid'
        container
        display='flex'
        direction='column'
        alignItems={'center'}
      >
        <TextField
          ref={ref}
          disabled={true}
          data-cy={'check-in-bib'}
          value={!dirtyBib ? 'No Bib' : dirtyBib}
          className={clsx(classes.bibField, classes.checkedInBib)}
        />
        <Typography className={classes.checkedInBibLabel}>
          Checked In
        </Typography>
      </Grid>
    )
  } else {
    const helperText = () => {
      if (isBibZeroError(bibFieldError)) {
        return 'Cannot assign Bib number 0'
      }
      if (isBibError(bibFieldError)) {
        return 'Bib is already assigned'
      }
      if (!askForTag && isTagError(tagFieldError)) {
        return 'Tag is already assigned'
      }
    }
    return (
      <>
        <TextField
          ref={ref}
          data-cy={'check-in-bib'}
          autoFocus={true}
          value={dirtyBib}
          onKeyDown={handleKeyDown}
          onChange={(e) => {
            dispatch(setDirtyBib(e.target.value))
            debounceDirtyBib(e.target.value)
          }}
          className={clsx(
            error && classes.bibError,
            classes.bibField,
            isMobile && classes.mobileBib
          )}
          error={error}
          helperText={helperText()}
          type={'text'}
          inputMode={'numeric'}
          inputProps={{ pattern: '\\d*' }}
        />
        <ChronoIconButton
          onClick={() =>
            checkCameraAccess().catch((err) => {
              logger.error('error while opening QR', err)
            })
          }
          Icon={CameraAltOutlinedIcon}
        />
      </>
    )
  }
}
